import {type CSSProperties, type FC, type ReactNode, useContext} from 'react';
import {useSelector} from 'react-redux';
import {selectIsPremium} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {
  selectBackgroundColor,
  selectCurrentSection,
  selectPristineBackground,
  selectShouldTogglePremiumBackground,
  selectUsedPremiumFeature,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {silver} from '~libSass/base/_exports.sass';
import {useBackground} from '../components/Background/actions/useBackground';
import {BackgroundContainer} from '../components/Background/BackgroundContainer';
import {EnvelopeContainer} from '../components/Envelope/EnvelopeContainer';
import {EventCreateEditor} from '../components/EventCreateEditor';
import {RevealContainer} from '../components/Reveal/RevealContainer';
import {Sections} from '../constants';
import {EventCreateContext} from '../EventCreateContext';

// Move sectionComponentsMap outside the hook to avoid recreation on each render
const sectionComponentsMap: Record<string, FC<{children?: ReactNode}>> = {
  [Sections.UPLOAD_PHOTO]: EventCreateEditor as FC<{children?: ReactNode}>, // Remove typecast when this component is converted to TS
  [Sections.CARD_DESIGN]: EventCreateEditor as FC<{children?: ReactNode}>, // Remove typecast when this component is converted to TS
  [Sections.BACKGROUND]: BackgroundContainer,
  [Sections.ENVELOPE_LINER]: EnvelopeContainer,
  [Sections.REVEAL]: RevealContainer as FC<{children?: ReactNode}>, // Remove typecast when this component is converted to TS
};

export const useDesignBackground = () => {
  const {bkgColor} = useContext(EventCreateContext);
  const currentSection = useSelector(selectCurrentSection);
  const backgroundColor = useSelector(selectBackgroundColor);
  const isPremium = useSelector(selectIsPremium);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const pristineBackground = useSelector(selectPristineBackground);
  const shouldTogglePremiumBackground = useSelector(selectShouldTogglePremiumBackground);
  const isDesktop = useIsLargishAndUp();
  const {selectedBackground} = useBackground(!isDesktop);

  const Section = sectionComponentsMap[currentSection];

  // Set background css
  const style: CSSProperties = {};
  if (
    (isPremium || usedPremiumFeature || !pristineBackground || shouldTogglePremiumBackground) &&
    !selectedBackground
  ) {
    style.backgroundColor = (!!backgroundColor && bkgColor) || silver;
  } else {
    style.backgroundColor = silver;
  }

  return {
    currentSection,
    Section,
    isDesktop,
    style,
    selectedBackground,
  };
};
